/* eslint-disable react-hooks/exhaustive-deps */
import { Box, styled, Typography, useTheme } from '@mui/material';
import React, { useEffect, useMemo, useRef, useState } from 'react';

// Card variant container
const StyledCardTabs = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  gap: theme.spacing(1),
  width: '100%',
  position: 'relative',
  backgroundColor: 'transparent',
  borderRadius: theme.spacing(2),
  border: `1px solid ${theme.palette.divider}`,
}));

// Card variant sliding indicator (pink border)
const SlidingIndicator = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: -1,
  bottom: -1,
  border: `2px solid ${theme.palette.pink[300]}`,
  borderRadius: theme.spacing(2),
  backgroundColor: 'transparent',
  boxSizing: 'border-box',
  transition: theme.transitions.create([
    'left',
    'width',
  ], {
    duration: theme.transitions.duration.standard,
    easing: theme.transitions.easing.easeInOut,
  }),
  zIndex: 2,
  pointerEvents: 'none',
}));

// Card variant tab
const StyledCardTab = styled(Box)<{
  isActive?: boolean;
  textColor?: string;
  maxWidth?: string;
}>(({ theme, isActive, textColor, maxWidth }) => ({
  flex: 1,
  maxWidth: maxWidth,
  overflow: 'hidden',
  cursor: 'pointer',
  padding: theme.spacing(2),
  textAlign: 'center',
  position: 'relative',
  zIndex: 3,
  transition: theme.transitions.create(['border-color', 'box-shadow'], {
    duration: theme.transitions.duration.short,
  }),
  '&:hover': {
    borderColor: theme.palette.pink[100],
  },
}));

// Card value display
const CardValue = styled(Box)<{ textColor?: string }>(({ theme, textColor }) => ({
  fontWeight: 'bold',
  color: textColor || theme.palette.text.primary,
  marginBottom: theme.spacing(0.5),
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
}));

export interface CardTabItem {
  value: string | number;
  tabKey: string;
  label?: string;
  color?: 'error' | 'success' | 'primary' | 'secondary';
  maxWidth?: string;
}

// Interface for expense card tabs props
export interface CardTabsProps {
  tabs: CardTabItem[];
  activeTabId: string | number;
  onChange: (event: React.SyntheticEvent, newValue: string | number) => void;
  isLoading?: boolean;
  error?: string | null;
}

const CardTabs: React.FC<CardTabsProps> = ({
  tabs,
  activeTabId,
  onChange,
  isLoading = false,
  error = null,
}) => {
  const theme = useTheme();
  const tabsRef = useRef<HTMLDivElement>(null);
  const [indicatorStyle, setIndicatorStyle] = useState<{
    left: number;
    width: number;
  }>({ left: 0, width: 0 });

  // Default to first tab if activeTabId is not provided or not found
  const effectiveActiveTabId = useMemo(() => {
    if (!tabs.length) return null;

    // Check if activeTabId exists in tabs
    const tabExists = tabs.some(tab => tab.tabKey === activeTabId);

    // Return first tab value if activeTabId doesn't exist or is null/undefined
    return tabExists ? activeTabId : tabs[0].tabKey;
  }, [tabs, activeTabId]);

  // Only notify parent when component first mounts and default selection is needed
  const hasInitialized = useRef(false);
  useEffect(() => {
    if (!hasInitialized.current && effectiveActiveTabId !== activeTabId && effectiveActiveTabId !== null) {
      hasInitialized.current = true;
      // Only trigger onChange on initial mount, not on subsequent changes
      const timer = setTimeout(() => {
        onChange({} as React.SyntheticEvent, effectiveActiveTabId);
      }, 0);
      return () => clearTimeout(timer);
    }
  }, [effectiveActiveTabId, activeTabId]); // Keep dependencies but use ref to prevent multiple calls

  const getTabColor = (color: CardTabItem['color']) => {
    switch (color) {
      case 'error':
        return theme.palette.error.main;
      case 'success':
        return theme.palette.success.main;
      case 'primary':
        return theme.palette.primary.main;
      case 'secondary':
        return theme.palette.text.secondary;
      default:
        return color;
    }
  };

  const updateIndicatorPosition = () => {
    if (!tabsRef.current) return;

    const activeTabIndex = tabs.findIndex(tab => tab.tabKey === effectiveActiveTabId);
    if (activeTabIndex === -1) return;

    const tabElements = tabsRef.current.querySelectorAll('[data-tab-id]');
    const activeTab = tabElements[activeTabIndex] as HTMLElement;

    if (activeTab) {
      // Use offsetLeft and offsetWidth instead of getBoundingClientRect to avoid issues during scroll
      const left = activeTab.offsetLeft;
      const width = activeTab.offsetWidth;

      setIndicatorStyle({
        left,
        width,
      });
    }
  };

  useEffect(() => {
    updateIndicatorPosition();
  }, [effectiveActiveTabId, tabs]);

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <Box>Loading...</Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box textAlign="center" py={4}>
        <Box color="error.main">{error}</Box>
      </Box>
    );
  }

  return (
    <Box>
      <StyledCardTabs ref={tabsRef}>
        <SlidingIndicator
          sx={{
            left: `${indicatorStyle.left}px`,
            width: `${indicatorStyle.width}px`,
          }}
        />
        {tabs.map((tab, index) => {
          const isActive = tab.tabKey === effectiveActiveTabId;
          const textColor = getTabColor(tab.color);
          const displayLabel = tab.label;

          return (
            <StyledCardTab
              key={tab.tabKey}
              data-tab-id={tab.tabKey}
              isActive={isActive}
              textColor={textColor}
              maxWidth={tab.maxWidth || '33.33%'}
              onClick={(e) => {
                onChange(e as React.SyntheticEvent, tab.tabKey)
              }}
            >
              <CardValue textColor={textColor}>
                {tab.value}
              </CardValue>
              <Typography
                noWrap
                color="text.primary"
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {displayLabel}
              </Typography>
            </StyledCardTab>
          );
        })}
      </StyledCardTabs>
    </Box>
  );
};

export default CardTabs; 